import { CONSTITUENT_SECTORS } from "./constituent-sectors";
import type { Holding, Sector } from "./fund-detail-types";

/**
 * Consistent color palette for sector names. Ported from the dane
 * etf-website data layer.
 */
const SECTOR_COLORS: Record<string, string> = {
  "Information Technology": "#007c44",
  Technology: "#007c44",
  Healthcare: "#9b7ddb",
  "Health Care": "#9b7ddb",
  Financials: "#848484",
  Finance: "#848484",
  "Consumer Discretionary": "#00c878",
  "Consumer Disc.": "#00c878",
  "Communication Services": "#4a80d4",
  "Comm. Services": "#4a80d4",
  Industrials: "#1a2888",
  Energy: "#485465",
  Materials: "#8385b6",
  "Consumer Staples": "#b87333",
  "Real Estate": "#2c3e50",
  Utilities: "#00b29e",
  "Professional Services": "#4a80d4",
  Manufacturing: "#cd7f32",
  "Mining & Extraction": "#7d5a0b",
  "Precious Metals": "#d4a520",
  "Fixed Income": "#5d8aa8",
  Commodities: "#8b6914",
  Options: "#ff5500",
  "Cash & Equivalents": "#ff5500",
  "Cash & Other": "#ff5500",
  Cash: "#ff5500",
  Other: "#6b6259",
  "FLEX Options": "#ff5500",
  "FLEX Options on SPY": "#ff5500",
  "FLEX Options on QQQ": "#ff5500",
  "FLEX Options on EEM": "#ff5500",
  "FLEX Options on EFA": "#ff5500",
  "FLEX Options on IWM": "#ff5500",
  "U.S. Treasury / Money Market": "#007c44",
};

const FALLBACK_COLORS = [
  "#2196F3",
  "#e67e22",
  "#1abc9c",
  "#e74c3c",
  "#795548",
  "#607D8B",
  "#8e44ad",
  "#16a085",
] as const;

function resolveSectorName(holding: Holding): string {
  return CONSTITUENT_SECTORS[holding.ticker] ?? holding.sector ?? "Other";
}

export function computeSectorsFromHoldings(holdings: Holding[]): Sector[] {
  if (holdings.length === 0) return [];

  const map = new Map<string, { pct: number; count: number }>();

  for (const h of holdings) {
    const key = resolveSectorName(h);
    const cur = map.get(key);
    const pct = h.pct ?? 0;
    if (cur) {
      cur.pct += pct;
      cur.count += 1;
    } else {
      map.set(key, { pct, count: 1 });
    }
  }

  let fallbackIdx = 0;

  const sectors = Array.from(map.entries())
    .map(
      ([name, { pct, count }]): Sector => ({
        name,
        pct: Math.round(pct * 100) / 100,
        companies: count,
        color:
          SECTOR_COLORS[name] ??
          FALLBACK_COLORS[fallbackIdx++ % FALLBACK_COLORS.length] ??
          "#6b6259",
      }),
    )
    .sort((a, b) => b.pct - a.pct);

  if (sectors.length > 0) {
    const total = sectors.reduce((s, x) => s + x.pct, 0);
    if (total > 0 && Math.abs(total - 100) > 0.001) {
      const scale = 100 / total;
      for (const s of sectors) {
        s.pct = Math.round(s.pct * scale * 100) / 100;
      }
    }
    const sumAfter = sectors.reduce((s, x) => s + x.pct, 0);
    const diff = Math.round((100 - sumAfter) * 100) / 100;
    if (diff !== 0 && sectors[0]) {
      sectors[0].pct = Math.round((sectors[0].pct + diff) * 100) / 100;
    }
  }
  return sectors;
}

/**
 * Resolve the sector data for a fund.
 *
 * Priority:
 *  1. Explicit `sectors` override (buffer instrument allocation, etc.)
 *  2. Computed from `holdings` via the constituent → sector map
 *  3. Empty array if neither
 */
export function resolveSectors(sectors: Sector[], holdings: Holding[]): Sector[] {
  if (sectors.length > 0) return sectors;
  return computeSectorsFromHoldings(holdings);
}

export function getSectorColor(sectorName: string): string {
  return SECTOR_COLORS[sectorName] ?? "#6b6259";
}
